import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "@/lib/safeDespia";
import { toast } from "sonner";

/**
 * On native app launch (and on auth change), check the server for any
 * pending friend invite that was created from the same IP via the web
 * "Add Friend" button. If found, the friendship is created server-side
 * and we navigate to the inviter's profile so the user has context.
 *
 * This bridges the gap that the Despia custom URL scheme (spotrai://...)
 * opens the app but does not forward the deep-link path into the webview.
 */
export const FriendIntentClaimer = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const claimedRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    if (!isNativeApp()) return; // web sessions don't need to claim
    if (claimedRef.current) return;
    claimedRef.current = true;

    let cancelled = false;
    const run = async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "claim-friend-intent",
          { body: {} },
        );
        if (cancelled || error || !data?.claimed) return;

        const name = data.inviter_username || "your friend";
        if (data.already_friends) {
          toast.success(`You're already friends with ${name}`);
        } else if (data.already_pending) {
          toast.info(`Friend request to ${name} is already pending`);
        } else {
          toast.success(`Friend request sent to ${name}!`);
        }
        if (data.inviter_username) {
          navigate(`/u/${data.inviter_username}`);
        }
      } catch {
        // silent — best-effort
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate]);

  return null;
};
